import React, { useMemo } from 'react';
import { Box, Button, Link, Typography } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DataTable from '../../../sharedModules/dataTable';
import { StatementInvoice } from '../model';
import { CollectionInvoicePdf } from '../../clients/model';
import { formatDollar, formatOptionalDate } from '../../clients/utils/format';
import { apiAbsoluteUrl } from '../../clients/utils/pdf';
import { useInvoicePdfUpload } from '../../clients/hooks/useInvoicePdfUpload';

interface Props {
  invoices: StatementInvoice[];
  location: string;
  acctNo: string;
  onUploaded: (applyTo: string, pdf: CollectionInvoicePdf) => void;
}

const buildColumns = (
  triggerUpload: (applyTo: string) => void,
  uploadingApplyTo: string | null,
  canUpload: boolean,
) => [
  { name: 'Invoice #', selector: (row: StatementInvoice) => row.apply_to, width: '130px' },
  {
    name: 'Invoice Date',
    selector: (row: StatementInvoice) => formatOptionalDate(row.invoice_date),
    width: '120px',
  },
  {
    name: 'Due Date',
    selector: (row: StatementInvoice) => formatOptionalDate(row.due_date),
    width: '120px',
  },
  {
    name: 'Amount',
    cell: (row: StatementInvoice) => <Typography variant="body2">{formatDollar(row.amount)}</Typography>,
    right: true,
    width: '120px',
  },
  {
    name: 'Balance',
    cell: (row: StatementInvoice) => (
      <Typography variant="body2" sx={{ color: row.balance > 0 ? 'error.main' : 'text.primary' }}>
        {formatDollar(row.balance)}
      </Typography>
    ),
    right: true,
    width: '120px',
  },
  {
    name: 'PDF',
    grow: 2,
    cell: (row: StatementInvoice) =>
      row.has_pdf && row.pdf ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PictureAsPdfIcon sx={{ color: '#e53935', fontSize: 18, flexShrink: 0 }} />
          {row.pdf.download_url ? (
            <Link
              href={apiAbsoluteUrl(row.pdf.download_url)}
              download={row.pdf.filename}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ wordBreak: 'break-all' }}
            >
              {row.pdf.filename}
            </Link>
          ) : (
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {row.pdf.filename}
            </Typography>
          )}
        </Box>
      ) : (
        <Button
          size="small"
          variant="outlined"
          startIcon={<UploadFileIcon />}
          disabled={!canUpload || uploadingApplyTo === row.apply_to}
          onClick={() => triggerUpload(row.apply_to)}
        >
          {uploadingApplyTo === row.apply_to ? 'Uploading…' : 'Upload Invoice'}
        </Button>
      ),
  },
];

const StatementInvoicesTable: React.FC<Props> = ({ invoices, location, acctNo, onUploaded }) => {
  const { fileInputRef, triggerUpload, handleFileSelected, uploadingApplyTo, canUpload } = useInvoicePdfUpload({
    location,
    acctNo,
    onUploaded,
  });

  const columns = useMemo(
    () => buildColumns(triggerUpload, uploadingApplyTo, canUpload),
    [triggerUpload, uploadingApplyTo, canUpload],
  );

  return (
    <>
      <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" hidden onChange={handleFileSelected} />
      {invoices.length === 0 ? (
        <Box sx={{ p: 3, color: 'text.secondary' }}>No invoices for this account.</Box>
      ) : (
        <DataTable
          keyField="apply_to"
          columns={columns}
          data={invoices}
          highlightOnHover
          striped
          noHeader
          dense
          pagination
          paginationPerPage={25}
          paginationRowsPerPageOptions={[25, 50, 100]}
        />
      )}
    </>
  );
};

export default StatementInvoicesTable;
